'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ContactUsAPI } from "@/lib/api";
import { toast } from "sonner";

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      const payload = {
        firstName: formData.name.split(" ")[0] || "",
        lastName: formData.name.split(" ").slice(1).join(" ") || "",
        email: formData.email,
        phone: formData.phone,
        company: "Website",

        // fallback values
        property_type: "inquiry",
        property_id: "",
        project_id: "",
        agent_id: "",

        budget: "Not Specified",
        timeline: "Flexible",

        message: `${formData.subject}\n\n${formData.message}`,
      };

      const res = await ContactUsAPI.create(payload);

      if (res.meta.status) {
        toast.success("Enquiry submitted successfully!");

        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        toast.error(res.meta.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden h-[50vh]">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-secondary/60 z-10"></div>
          <img
            src="/images/dubai-marina.jpg"
            alt="Dubai Marina"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="relative h-full z-20 max-w-7xl mx-auto flex items-center justify-center text-center">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Ready to experience luxury living? Have questions about our properties?
              We're here to help make your dream vacation a reality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <motion.div
              variants={staggerChildren}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <motion.div variants={fadeInUp}>
                <h2 className="text-4xl font-bold mb-6 text-gray-900">Contact Information</h2>
                <p className="text-lg text-gray-600 mb-10">
                  We're here to assist you with any questions about our luxury properties,
                  booking inquiries, or special requests. Reach out to us through any of the channels below.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#FFC400]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#FFC400]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Phone</h3>
                    <p className="text-gray-600 text-lg">+971524227546</p>
                    <p className="text-gray-500 text-sm">Available 24/7 for urgent inquiries</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#FFC400]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#FFC400]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Email</h3>
                    <p className="text-gray-600 text-lg">info@18bricksholidayhomes.ae</p>
                    <p className="text-gray-500 text-sm">We respond within 2 hours during business hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#FFC400]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#FFC400]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Office Location</h3>
                    <p className="text-gray-600 text-lg">Quarter A, 102, Expo City Dubai, 8 Mangrove Avenue, Dubai, UAE</p>
                    <p className="text-gray-500 text-sm">Visit us for in-person consultations</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#FFC400]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#FFC400]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Business Hours</h3>
                    <p className="text-gray-600 text-lg">Monday - Friday: 9:00 AM - 6:00 PM</p>
                    <p className="text-gray-500 text-sm">Saturday-Sunday: Closed</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <Card className="shadow-xl border-0">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-gray-900">Send us a Message</h3>

                  {isSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-8"
                    >
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h4>
                      <p className="text-gray-600">Thank you for contacting us. We'll get back to you soon.</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-2 block">
                            Full Name *
                          </Label>
                          <Input
                            value={formData.name}
                            onChange={(e) =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                            Email Address *
                          </Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              setFormData({ ...formData, email: e.target.value })
                            }
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-2 block">
                            Phone Number
                          </Label>
                          <Input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) =>
                              setFormData({ ...formData, phone: e.target.value })
                            }
                          />
                        </div>
                        {/* <div>
                          <Label htmlFor="inquiryType" className="text-sm font-medium text-gray-700 mb-2 block">
                            Inquiry Type
                          </Label>
                          <Select value={formData.inquiryType} onValueChange={(value) => handleInputChange('inquiryType', value)}>
                            <SelectTrigger className="h-12">
                              <SelectValue placeholder="Select inquiry type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="booking">Property Booking</SelectItem>
                              <SelectItem value="inquiry">General Inquiry</SelectItem>
                              <SelectItem value="partnership">Partnership</SelectItem>
                              <SelectItem value="support">Customer Support</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div> */}
                      </div>

                      <div>
                        <Label htmlFor="subject" className="text-sm font-medium text-gray-700 mb-2 block">
                          Subject *
                        </Label>
                        <Input
                          value={formData.subject}
                          onChange={(e) =>
                            setFormData({ ...formData, subject: e.target.value })
                          }
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-sm font-medium text-gray-700 mb-2 block">
                          Message *
                        </Label>
                        <Textarea
                          value={formData.message}
                          onChange={(e) =>
                            setFormData({ ...formData, message: e.target.value })
                          }
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {loading ? "Submitting..." : "Submit Enquiry"}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-100 dark:bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900 dark:text-white">
              Find Us
            </h2>

            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Visit our office in the heart of Business Bay, Dubai. Our team is
              available for personalized consultations and property discussions.
            </p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="overflow-hidden rounded-3xl shadow-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#121212]"
          >
            {/* <iframe
              src="https://www.google.com/maps?q=Blue+Bay+Tower+Business+Bay+Dubai&output=embed"
              width="100%"
              height="500"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
            /> */}

            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3617.19295267459!2d55.1524928!3d24.959549!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6b5bce7049d5%3A0x4774e3cae6705c2c!2s18Bricks%C2%AE%20Group!5e0!3m2!1sen!2sin!4v1782712378527!5m2!1sen!2sin"
              width="100%"
              height="500"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
            ></iframe>

            <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-6">


              <a
                href="https://maps.app.goo.gl/93ESsFQ1KZXHXvCE7"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#c89a58] text-white hover:bg-[#b58a47] transition-colors"
              >
                <MapPin className="w-5 h-5" />
                Open in Google Maps
              </a>
            </div>
          </motion.div>
        </div>
      </section >
      {/* FAQ Section */}
      < section className="py-20" >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">
              Quick answers to common questions about our services and properties.
            </p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="w-full space-y-4">
              {[
                {
                  question: "How quickly do you respond to inquiries?",
                  answer: "We typically respond to all inquiries within 2 hours during business hours and within 24 hours for inquiries received outside business hours."
                },
                {
                  question: "Can I schedule a property viewing?",
                  answer: "Yes, we offer property viewings by appointment. Contact us to schedule a convenient time to visit any of our available properties."
                },
                {
                  question: "Do you offer concierge services?",
                  answer: "Absolutely! Our concierge team can assist with airport transfers, restaurant reservations, activity bookings, and other personalized services."
                },
                {
                  question: "What payment methods do you accept?",
                  answer: "We accept all major credit cards, bank transfers, and secure online payment gateways. We also offer flexible payment plans for longer stays."
                }
              ].map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-[#FFC400] transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section >
    </div >
  );
}
